import OrganizationApi from 'app/api/OrganizationApi';
import useIsMounted from 'app/hooks/useIsMounted';
import cx from 'classnames';
import { UserRole, useMeQuery } from 'legoland-sdk/dist/experimental';
import { useContext, useEffect, useState } from 'react';
import { useToasts } from 'tombac';
import { ChevronRightIcon, UserIcon } from 'tombac-icons';
import OrganizationChildrenSummary from "./OrganizationChildrenSummary";
import OrganizationMoveToModal from "./OrganizationMoveToModal";
import OrganizationNameEditor from "./OrganizationNameEditor";
import { OrganizationSapId } from "./OrganizationSapId";
import { TransferContext } from "./OrganizationTransferContext";
import OrganizationTree from "./OrganizationTree";
import { useOrgTreeContext } from "./OrganizationTreeContext";
import "./OrganizationTreeItem.css";
import OrganizationTreeItemMenu from "./OrganizationTreeItemMenu";
import OrganizationUsersSummary from "./OrganizationUsersSummary";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function findOrgInTree(orgId, tree) {
  let foundOrg = tree.find(org => org.id === orgId);
  if (!foundOrg) {
    for (const treeNode of tree) {
      foundOrg = findOrgInTree(orgId, treeNode.children);
      if (foundOrg) break;
    }
  }
  return foundOrg;
}
function isOrgChildOf(orgId, childOf) {
  return childOf.children.reduce((isChild, child) => isChild || child.id === orgId || isOrgChildOf(orgId, child), false);
}
export default function OrganizationTreeItem(_ref) {
  let {
    initalExpanded = false,
    onChange,
    org,
    treeCommand
  } = _ref;
  const mounted = useIsMounted();
  const {
    addToast
  } = useToasts();
  const meQuery = useMeQuery();
  const currentUser = meQuery.data?.me;
  const fullTree = useOrgTreeContext();
  const canHaveChildren = org => org.depth < (currentUser?.role === UserRole.Admin ? 2 : 1);
  const disableChildCreation = !canHaveChildren(org) || currentUser?.role === UserRole.Support;
  const [expanded, setExpanded] = useState(initalExpanded);
  const expandable = org.children.length > 0;
  useEffect(() => {
    if (treeCommand) {
      if (treeCommand.type === 'expand') setExpanded(true);else if (treeCommand.type === 'collapse') setExpanded(false);
    }
  }, [treeCommand]);
  const [editing, setEditing] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const handleDelete = async () => {
    setDeleted(true);
    try {
      await OrganizationApi.delete(org.id);
    } catch (error) {
      console.error(error);
      addToast(error?.message || error, 'danger');
    }
    if (mounted && onChange) onChange();
  };
  const [isMoveToOpen, setIsMoveToOpen] = useState(false);
  const handleMoveTo = () => {
    setIsMoveToOpen(true);
  };
  const moveToOptionFilter = optionOrg => {
    const targetOrg = findOrgInTree(optionOrg.id, fullTree);
    return canHaveChildren(targetOrg) && !isOrgChildOf(optionOrg.id, org);
  };
  const shouldShowMoveTo = currentUser?.role === UserRole.Admin && !editing && Boolean(org.parent);
  const {
    setOrganization: setTransferOrganization,
    setIsInvitationModalOpen
  } = useContext(TransferContext);
  const handleTransferUser = () => {
    setTransferOrganization(org);
    setIsInvitationModalOpen(true);
  };
  const handleMove = async (orgId, orgName, newParent) => {
    try {
      await OrganizationApi.update(orgId, {
        name: orgName,
        parentId: newParent
      });
    } catch (error) {
      console.error(error);
      addToast(error?.message || error, 'danger');
    }
    if (mounted.current && onChange) onChange();
  };
  return /*#__PURE__*/_jsxs("li", {
    className: cx('OrganizationTreeItem', deleted && 'OrganizationTreeItem--deleted'),
    "data-sentry-component": "OrganizationTreeItem",
    "data-sentry-source-file": "OrganizationTreeItem.tsx",
    children: [/*#__PURE__*/_jsx(OrganizationMoveToModal, {
      isOpen: isMoveToOpen,
      onClose: () => setIsMoveToOpen(false),
      onMove: newParent => handleMove(org.id, org.name, newParent),
      optionFilter: moveToOptionFilter,
      org: org,
      "data-sentry-element": "OrganizationMoveToModal",
      "data-sentry-source-file": "OrganizationTreeItem.tsx",
      "data-sentry-element": "OrganizationMoveToModal"
    }), expandable && /*#__PURE__*/_jsx("button", {
      className: cx('OrganizationTreeItem__expand', expanded && 'OrganizationTreeItem__expand--expanded'),
      onClick: () => setExpanded(!expanded),
      children: /*#__PURE__*/_jsx(ChevronRightIcon, {
        className: "OrganizationTreeItem__expand-icon",
        fixedWidth: true
      })
    }), ' ', /*#__PURE__*/_jsxs("div", {
      className: cx('OrganizationTreeItem__header'),
      children: [/*#__PURE__*/_jsx("span", {
        className: "OrganizationTreeItem__name",
        children: currentUser?.role === UserRole.Support ? org.name : /*#__PURE__*/_jsx(OrganizationNameEditor, {
          name: org.name,
          onBlur: () => setEditing(false),
          onFocus: () => setEditing(true),
          onNameChange: onChange,
          orgId: org.id,
          readOnly: !org.parent
        })
      }), ' ', /*#__PURE__*/_jsxs("span", {
        className: "OrganizationTreeItem__summary",
        children: [/*#__PURE__*/_jsx(OrganizationChildrenSummary, {
          className: "OrganizationTreeItem__summary-item",
          disableAdd: disableChildCreation,
          onChange: onChange,
          org: org,
          "data-sentry-element": "OrganizationChildrenSummary",
          "data-sentry-source-file": "OrganizationTreeItem.tsx",
          "data-sentry-element": "OrganizationChildrenSummary"
        }), ' ', /*#__PURE__*/_jsx(OrganizationUsersSummary, {
          className: "OrganizationTreeItem__summary-item",
          org: org,
          "data-sentry-element": "OrganizationUsersSummary",
          "data-sentry-source-file": "OrganizationTreeItem.tsx",
          "data-sentry-element": "OrganizationUsersSummary"
        }), currentUser?.role === UserRole.Admin && /*#__PURE__*/_jsx(OrganizationSapId, {
          onChange: onChange,
          org: org
        }), ' ', org.parent && currentUser?.role === UserRole.Distributor && /*#__PURE__*/_jsx("span", {
          className: "OrganizationTreeItem_add-user",
          children: /*#__PURE__*/_jsx(UserIcon, {
            onClick: handleTransferUser,
            size: 1
          })
        })]
      }), /*#__PURE__*/_jsx("span", {
        className: "OrganizationTreeItem__actions",
        children: org.parent && currentUser?.role !== UserRole.Support && /*#__PURE__*/_jsx(OrganizationTreeItemMenu, {
          onDelete: handleDelete,
          onMoveTo: handleMoveTo,
          showDelete: !expandable && !deleted && org.users.length === 0 && org.contracts.length === 0,
          showMoveTo: shouldShowMoveTo
        })
      })]
    }), org.children.length > 0 && expanded && /*#__PURE__*/_jsx(OrganizationTree, {
      onChange: onChange,
      treeBranch: org.children,
      treeCommand: treeCommand
    })]
  });
}